import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';
import Breadcrumbs from '../../ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import navigation from '../../menu-items';
import { drawerWidth, SIDEBAR, SIDEBAR_STORAGE_KEY } from '../../store/constant';
import { SET_MENU } from '../../store/actions';
import { IconChevronRight } from '@tabler/icons';

const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'sidebarWidth' && prop !== 'resizing' && prop !== 'topBar'
})(({ theme, sidebarWidth, resizing, topBar }) => ({
  ...theme.typography.mainContent,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  transition: resizing ? 'none' : theme.transitions.create(['width', 'margin'], { duration: 200 }),
  [theme.breakpoints.up('md')]: {
    marginLeft: sidebarWidth ? 0 : '20px',
    width: `calc(100% - ${sidebarWidth}px)`,
    ...(!topBar && {
      marginTop: '10px',
      marginRight: '10px',
      marginBottom: '10px',
      minHeight: 'calc(100vh - 20px)',
      borderRadius: '16px'
    })
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '20px',
    width: `calc(100% - ${drawerWidth}px)`,
    padding: '16px'
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: '10px',
    width: `calc(100% - ${drawerWidth}px)`,
    padding: '16px',
    marginRight: '10px'
  }
}));

const MainLayout = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const sidebarOpened = useSelector((state) => state.customization.opened);
  const sidebarWidth = useSelector((state) => state.customization.sidebarWidth);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [userData, setUserData] = useState(null);

  const handleLeftDrawerToggle = () => {
    if (matchDownMd) setMobileOpen((open) => !open);
    else dispatch({ type: SET_MENU, opened: !sidebarOpened });
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (matchDownMd) return;
    try {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify({ opened: sidebarOpened, width: sidebarWidth }));
    } catch (e) {
    }
  }, [sidebarOpened, sidebarWidth, matchDownMd]);

  useEffect(() => {
    const fetchData = async () => {
      await retrieveUserData();
    };

    fetchData();
  }, []);

  const retrieveUserData = async () => {
    await axios
      .get('/api/getUserDataFromSession')
      .then((res) => {
        setUserData(res.data.response);
      })
      .catch((error) => {
        console.error(error.response?.data);
      });
  };

  const hasSidebar = !userData?.role.includes('organization');
  const currentSidebarWidth = hasSidebar ? (sidebarOpened ? sidebarWidth : SIDEBAR.collapsedWidth) : 0;
  const topBar = !hasSidebar || matchDownMd;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {topBar && (
        <AppBar
          enableColorOnDark
          position="fixed"
          color="inherit"
          elevation={0}
          sx={{
            bgcolor: theme.palette.background.default
          }}
        >
          <Toolbar>
            <Header
              handleLeftDrawerToggle={handleLeftDrawerToggle}
              userData={userData}
              showBrand
              showMenuButton={hasSidebar && matchDownMd}
              showUserControls
            />
          </Toolbar>
        </AppBar>
      )}

      {/* drawer */}
      {hasSidebar && (
        <Sidebar
          drawerOpen={mobileOpen}
          drawerToggle={handleLeftDrawerToggle}
          onResizeChange={setResizing}
          userData={userData}
        />
      )}

      {/* main content */}
      <Main theme={theme} sidebarWidth={currentSidebarWidth} resizing={resizing} topBar={topBar}>
        {/* breadcrumb */}
        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
        <Outlet />
      </Main>
    </Box>
  );
};

export default MainLayout;